import { useCallback, useMemo, useState } from 'react';
import { Pagination as PaginationComponent, Select, Spinner } from '@paysera/ui';
import { useTranslation } from 'react-i18next';
import { PageLoader } from '@app/components/PageLoader';
import { Table } from '@app/components/Table';
import { useIsMobile } from '@app/utils/isMobile';
import { PER_PAGE_OPTIONS } from '@modules/investor/config/table';
import Header from './Header';
import InvestorTableMobile from './InvestorTableMobile';
const InvestorTable = ({ data, config, pagination, onPageChange, onPerPageChange, onDetailsClick, initialLoading, isFetching = false, onSortChange, onFilterChange, filters, }) => {
    const { t } = useTranslation();
    const [isMobile] = useIsMobile();
    const [sortBy, setSortBy] = useState([]);
    const paginationConfig = {
        onPageChange,
        pageIndex: pagination.page,
        pageSize: pagination.perPage,
        pageCount: pagination.totalPages,
        total: pagination.totalItems,
        locale: {
            details: {
                showing: t('financing.pagination.showing'),
                of: t('financing.pagination.of'),
                items: '',
            },
        },
    };
    const totalPages = Math.ceil(pagination.totalItems / pagination.perPage);
    const fromItem = (pagination.page - 1) * pagination.perPage + 1;
    const toItem = Math.min(pagination.page * pagination.perPage, pagination.totalItems);
    const perPageOptions = useMemo(() => PER_PAGE_OPTIONS.map((option) => ({ value: String(option), label: String(option) })), []);
    const paginationFooter = (<div className="flex w-full items-center justify-between px-4 py-3">
            <div className="flex items-center gap-4">
                <div className="w-32">
                    <Select label={t('financing.pagination.items_per_page')} size="md" isClearable={false} options={perPageOptions} value={String(pagination.perPage)} onChange={(value) => onPerPageChange === null || onPerPageChange === void 0 ? void 0 : onPerPageChange(Number(Array.isArray(value) ? value[0] : value))}/>
                </div>
                <span className="body-s text-neutral-secondary-initial">
                    {t('financing.pagination.showing')} {fromItem}-{toItem} {t('financing.pagination.of')}{' '}
                    {pagination.totalItems}
                </span>
            </div>
            <PaginationComponent currentPage={pagination.page} totalPages={totalPages} onPageChange={onPageChange}/>
        </div>);
    const mappedData = config.mapDataToColumns(data, t);
    const handleSortChange = useCallback((newSortBy) => {
        setSortBy(newSortBy);
        if (newSortBy.length > 0 && onSortChange) {
            const [{ id, desc }] = newSortBy;
            onSortChange(id, desc ? 'desc' : 'asc');
        }
    }, [onSortChange]);
    const handleFilterChange = useCallback((newFilters) => {
        if (onFilterChange) {
            onFilterChange(newFilters);
        }
    }, [onFilterChange]);
    if (initialLoading) {
        return <PageLoader />;
    }
    return (<div className="relative">
            {isMobile ? (<InvestorTableMobile data={data} config={config} pagination={paginationConfig} onPageChange={onPageChange} onDetailsClick={onDetailsClick} onFilterChange={handleFilterChange} filters={filters} isFetching={isFetching} showCreditScore={!config.isInvestment} showInvestmentStatus={config.isInvestment}/>) : (<>
                    <Table columns={config.columns} data={mappedData} headerContent={<Header onFilterChange={handleFilterChange} filters={filters} showCreditScore={!config.isInvestment} showInvestmentStatus={config.isInvestment}/>} pagination={{
                content: paginationFooter,
            }} sorting={{
                sortBy,
                onSortChange: handleSortChange,
            }} onRowClick={(row) => onDetailsClick(row.id)}/>
                    {isFetching && (<div className="absolute inset-0 bg-white/60 flex items-center justify-center" style={{ zIndex: 50 }}>
                            <div className="flex flex-wrap gap-2 justify-center">
                                <Spinner className="w-8 h-8"/>
                            </div>
                        </div>)}
                </>)}
        </div>);
};
export default InvestorTable;
